import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, Printer, ShieldAlert, Tag, Undo2, Banknote } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import type { ParcelCard } from 'shared/api'
import { trpc } from 'shared/api'
import { DamageAct } from 'shared/components/damage-act'
import { HubLabelPrint } from 'shared/components/hub-label'
import { PageFrame } from 'shared/components/page-frame'
import { Hint } from 'shared/components/section'
import { Alert, AlertDescription, AlertTitle } from 'shared/components/ui/alert'
import {
	AlertDialog,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogHeader,
	AlertDialogTitle,
} from 'shared/components/ui/alert-dialog'
import { Badge } from 'shared/components/ui/badge'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from 'shared/components/ui/dialog'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from 'shared/components/ui/dropdown-menu'
import { Skeleton } from 'shared/components/ui/skeleton'
import { handoverCancelReasonKey } from 'shared/api'
import { useTranslate } from 'shared/lib/i18n'
import { actsAsAdmin, editsParcels } from 'shared/lib/roles'
import { useAuthStore } from 'shared/store/auth'
import { FoundAtCounterPanel } from './FoundAtCounterPanel'
import { HandoverCancelPanel } from './HandoverCancelPanel'
import { MarkersPanel } from './MarkersPanel'
import { ParcelEditPanel } from './ParcelEditPanel'
import { ParcelHistory } from './ParcelHistory'
import { CancelPaymentDialog, CancelUnavailable, CollectButton, PaymentError, PaymentStatus } from './PaymentPanel'
import { usePayment } from './use-payment'
import { ElsewhereParcel, WhereNow } from './WhereNow'
import { Fact, Facts } from 'shared/components/facts'
import { BackButton, MoreButton } from './parcel-parts'
import { focusFirstField } from './dialog-focus'
import { money, sum } from './lookup-words'

/**
 * A Parcel's card (spec §12, §22; ticket 48): its code and stage as the heading, the one
 * act its situation asks for at the header's right — «Принять наличные» while unpaid on
 * the shelf, «Править» for support — and the rare acts behind «⋯»: a problem mark, damage,
 * TezGo's label, cancelling the handover or the cash (each in its dialog, the
 * irreversible ones asking first). Under it the facts in one grid, then the history.
 *
 * Who may change a Parcel differs from who may read it (ADR-0029): a cashier and support
 * read it, but it offers them no payment step and no marks — `editsParcels` is that rule;
 * the corrections are the server's verdicts (`corrections`), the edit support's (`edits`).
 * Another Point's Parcel, to an operator, is only where it is (`ElsewhereParcel`).
 */

type FullCard = ParcelCard
type Dialogs = 'markers' | 'damage' | 'label' | 'handover' | null

export function ParcelDetail({ parcelId, onBack }: { parcelId: string; onBack: () => void }) {
	const { t } = useTranslate()
	const detailQuery = useQuery({
		...trpc.parcels.Detail.queryOptions({ parcelId }),
		retry: false,
	})
	const answer = detailQuery.data

	if (answer?.view === 'elsewhere') return <ElsewhereParcel parcel={answer} onBack={onBack} />
	if (answer?.view === 'full') {
		return <FullParcel key={parcelId} parcel={answer} onBack={onBack} onChanged={() => detailQuery.refetch()} />
	}
	return (
		<PageFrame back={<BackButton onBack={onBack} />} title='parcels.detailTitle'>
			{detailQuery.isError ? (
				<p className='text-sm text-destructive'>{t('parcels.detailNotFound')}</p>
			) : (
				<div className='flex flex-col gap-4' aria-label={t('parcels.detailLoading')}>
					<Skeleton className='h-6 w-1/3' />
					<Skeleton className='h-28 w-full' />
					<Skeleton className='h-48 w-full' />
				</div>
			)}
		</PageFrame>
	)
}

function FullParcel({ parcel, onBack, onChanged }: { parcel: FullCard; onBack: () => void; onChanged: () => void }) {
	const { t } = useTranslate()
	const role = useAuthStore((s) => s.user.role)
	const mayEdit = editsParcels(role)
	const [dialog, setDialog] = useState<Dialogs>(null)
	const close = () => setDialog(null)
	const done = () => {
		setDialog(null)
		onChanged()
	}

	const code = parcel.resolvingKeys[0]?.trackingCode ?? ''
	const payment = usePayment({
		parcelId: parcel.parcelId,
		state: parcel.state,
		payment: parcel.payment,
		amountOwed: parcel.amountOwed,
		readOnly: !mayEdit,
		cancelVerdict: parcel.corrections.cancelCash,
		onCollected: onChanged,
		onCancelled: onChanged,
	})

	// Support and the admin print any Parcel's label; an operator their own (the card is theirs).
	const printsLabel = role === 'support' || actsAsAdmin(role) || role === 'operator'
	// Damage is the Point operator's act here, on a Parcel TezGo holds (ticket 42).
	const recordsDamage =
		role === 'operator' &&
		!parcel.markers.includes('damaged') &&
		(parcel.state === 'ON_SHELF' || parcel.state === 'IN_NETWORK')
	// Cancelling a handover: offered only for a handed-over Parcel, and not at all to a role that never cancels.
	const handover = parcel.corrections.cancelHandover
	const handoverShown = parcel.state === 'HANDED_OVER' && handover.reason !== 'not_permitted'

	const rare: ReactNode[] = []
	if (printsLabel) {
		rare.push(
			<DropdownMenuItem key='label' data-testid='hub-label-open' onSelect={() => setDialog('label')}>
				<Printer />
				{t('hubLabel.printAgain')}
			</DropdownMenuItem>,
		)
	}
	if (mayEdit) {
		rare.push(
			<DropdownMenuItem key='markers' data-testid='markers-open' onSelect={() => setDialog('markers')}>
				<Tag />
				{t('parcels.markersOpen')}
			</DropdownMenuItem>,
		)
	}
	if (recordsDamage) {
		rare.push(
			<DropdownMenuItem key='damage' data-testid='damage-open' onSelect={() => setDialog('damage')}>
				<ShieldAlert />
				{t('damage.open')}
			</DropdownMenuItem>,
		)
	}
	const corrections: ReactNode[] = []
	if (handoverShown) {
		corrections.push(
			handover.allowed ? (
				<DropdownMenuItem
					key='handover'
					variant='destructive'
					data-testid='handover-cancel-open'
					onSelect={() => setDialog('handover')}
				>
					<Undo2 />
					{t('handoverCancel.submit')}
				</DropdownMenuItem>
			) : (
				<Unavailable key='handover' icon={<Undo2 />} title={t('handoverCancel.submit')}>
					<span data-testid='handover-cancel-unavailable' data-reason={handover.reason ?? ''}>
						{handover.reason ? t(handoverCancelReasonKey(handover.reason) as never) : ''}
					</span>
				</Unavailable>
			),
		)
	}
	if (payment.cancellable) {
		corrections.push(
			<DropdownMenuItem
				key='cash'
				variant='destructive'
				data-testid='payment-cancel-submit'
				onSelect={payment.askCancel}
			>
				<Banknote />
				{t('payment.cancelSubmit')}
			</DropdownMenuItem>,
		)
	} else if (payment.cancelUnavailable) {
		corrections.push(
			<Unavailable key='cash' icon={<Banknote />} title={t('payment.cancelSubmit')}>
				<CancelUnavailable model={payment} />
			</Unavailable>,
		)
	}

	const actions = (
		<>
			<CollectButton model={payment} />
			{/* Support's edit — the Point, the Recipient, the amount, with a comment (ticket 44). */}
			{parcel.edits && <ParcelEditPanel parcel={parcel} edits={parcel.edits} onEdited={onChanged} />}
			{rare.length + corrections.length > 0 && (
				<DropdownMenu modal={false}>
					<DropdownMenuTrigger asChild>
						<MoreButton testId='parcel-actions' />
					</DropdownMenuTrigger>
					<DropdownMenuContent align='end' className='w-64'>
						{rare}
						{rare.length > 0 && corrections.length > 0 && <DropdownMenuSeparator />}
						{corrections}
					</DropdownMenuContent>
				</DropdownMenu>
			)}
		</>
	)

	return (
		<PageFrame
			back={<BackButton onBack={onBack} />}
			heading={
				<>
					<span data-testid='parcel-code'>{code}</span>
					<Badge variant='secondary' data-testid='parcel-state'>
						{t(`parcelState.${parcel.state}` as never)}
					</Badge>
					{parcel.markers.map((marker) => (
						<Badge key={marker} variant='destructive'>
							{t(`parcelMarker.${marker}` as never)}
						</Badge>
					))}
				</>
			}
			actions={actions}
		>
			<PaymentError model={payment} />

			{parcel.doublePayment && (
				// A double payment on this Parcel (ADR-0046): the partner's payment is
				// recorded, inactive, over the cash taken at the counter.
				<Alert variant='destructive' data-testid='parcel-double-payment'>
					<AlertTriangle />
					<AlertTitle>{t('parcels.doublePaymentTitle')}</AlertTitle>
					<AlertDescription>
						{t('doublePayment.cardBanner', {
							cash: t('payment.som', { amount: sum(parcel.doublePayment.cash.amount) }),
							partner: money(t, parcel.doublePayment.partnerPayment),
						})}
					</AlertDescription>
				</Alert>
			)}

			{/* A Parcel declared lost, found here: the operator says so (ticket 43). */}
			{role === 'operator' && parcel.state === 'LOST' && (
				<FoundAtCounterPanel key={`found-${parcel.parcelId}`} parcelId={parcel.parcelId} onFound={onChanged} />
			)}

			<Facts testId='parcel-facts'>
				<Fact label={t('parcels.detailRecipient')}>{parcel.recipient.name}</Fact>
				<Fact label={t('where.title')}>
					<WhereNow
						where={parcel.whereNow}
						ended={!['EXPECTED', 'IN_NETWORK', 'ON_SHELF'].includes(parcel.state)}
					/>
				</Fact>
				<Fact label={t('payment.title')} testId='payment-panel'>
					<PaymentStatus model={payment} />
				</Fact>
				<Fact label={t('parcels.detailDestinationPoint')}>{parcel.destinationPointCode}</Fact>
				<Fact label={t('parcels.detailClientRef')}>{parcel.recipient.clientRef}</Fact>
				<Fact label={t('parcels.detailOrderRef')}>{parcel.partnerOrderRef}</Fact>
				{parcel.recipientDocument && (
					// A by-document partner's Recipient (ADR-0031): the full number is
					// support's alone; everyone else reads its last four characters.
					<Fact label={t('parcels.detailDocument')}>
						<span data-testid='parcel-document'>
							{t(`policies.documentType_${parcel.recipientDocument.type}`)}:{' '}
							{parcel.recipientDocument.value ?? parcel.recipientDocument.mask}
						</span>
					</Fact>
				)}
				{parcel.resolvingKeys.length > 1 && (
					<Fact label={t('parcels.detailResolvingKeys')}>
						<span className='font-mono'>{parcel.resolvingKeys.map((k) => k.trackingCode).join(', ')}</span>
					</Fact>
				)}
			</Facts>

			<ParcelHistory history={parcel.history} />

			<Dialog open={dialog === 'markers'} onOpenChange={(open) => !open && close()}>
				<DialogContent className='sm:max-w-lg' onOpenAutoFocus={focusFirstField}>
					<DialogHeader>
						<DialogTitle className='flex items-center gap-2'>
							{t('markers.title')}
							<Hint>{t('markers.explain')}</Hint>
						</DialogTitle>
						<DialogDescription className='sr-only'>{t('markers.explain')}</DialogDescription>
					</DialogHeader>
					<MarkersPanel parcelId={parcel.parcelId} markers={parcel.markers} onChanged={done} />
				</DialogContent>
			</Dialog>

			<Dialog open={dialog === 'damage'} onOpenChange={(open) => !open && close()}>
				<DialogContent className='sm:max-w-lg'>
					<DialogHeader>
						<DialogTitle>{t('damage.title', { code })}</DialogTitle>
						<DialogDescription className='sr-only'>{t('damage.comment')}</DialogDescription>
					</DialogHeader>
					<DamageAct bare parcelId={parcel.parcelId} code={code} onDone={done} onCancel={close} />
				</DialogContent>
			</Dialog>

			<Dialog open={dialog === 'label'} onOpenChange={(open) => !open && close()}>
				<DialogContent className='sm:max-w-md' aria-describedby={undefined}>
					<DialogHeader>
						<DialogTitle>{t('parcels.labelTitle')}</DialogTitle>
					</DialogHeader>
					<LabelToPrint parcelId={parcel.parcelId} />
				</DialogContent>
			</Dialog>

			<AlertDialog open={dialog === 'handover'} onOpenChange={(open) => !open && close()}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t('handoverCancel.ask', { code })}</AlertDialogTitle>
						<AlertDialogDescription>{t('handoverCancel.explain')}</AlertDialogDescription>
					</AlertDialogHeader>
					<HandoverCancelPanel
						parcelId={parcel.parcelId}
						state={parcel.state}
						verdict={handover}
						onCancelled={done}
						onClose={close}
					/>
				</AlertDialogContent>
			</AlertDialog>

			<CancelPaymentDialog model={payment} />
		</PageFrame>
	)
}

/** An act of the «⋯» menu not open now, with the server's reason under it. */
function Unavailable({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
	return (
		<div className='flex gap-2 px-2 py-1.5 text-sm [&_svg]:mt-0.5 [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-muted-foreground'>
			{icon}
			<div className='flex flex-col gap-0.5'>
				<span className='text-muted-foreground'>{title}</span>
				<span className='text-xs text-muted-foreground'>{children}</span>
			</div>
		</div>
	)
}

/** TezGo's label of this Parcel, asked of the server and sent to the printer as soon as it is drawn. */
function LabelToPrint({ parcelId }: { parcelId: string }) {
	const { t } = useTranslate()
	const query = useQuery({
		...trpc.parcels.HubLabel.queryOptions({ parcelId }),
		retry: false,
		staleTime: 0,
		gcTime: 0,
	})
	if (query.isError) {
		return (
			<p className='text-sm text-destructive' data-testid='hub-label-refused'>
				{t('hubLabel.refused')}
			</p>
		)
	}
	if (!query.data) return <Skeleton className='h-56 w-[360px]' />
	return <HubLabelPrint key={query.data.labelCode} label={query.data} printNow />
}
